import {useState} from 'react';
import PropTypes from 'prop-types';
import {Button} from 'react-bootstrap';
import StripeCheckout from './StripeCheckout';
import {confirmPayment, createPayment} from '../api/ReservationApi';
import {problemMessage} from '../api/client';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import {RELEASED_COPY, isReleasedConflict} from '../consultation/bookingState';

const PROCESSING = 'Payment is still processing. Please check again shortly.';

/**
 * Pays for a confirmed consultation that was booked but not paid (the patient paid later, from
 * the record). Same rules as the booking wizard: each request keeps its key, and a confirmation
 * that failed after Stripe took the card is retried by confirming again, not by paying again.
 * A confirmation answered 409 means the consultation was released first and the charge refunded:
 * that ends the flow with the released message (no retry), and `onReleased` lets the parent
 * refresh the reservation.
 */
export default function ReservationPayment({reservation, onPaid, onReleased}) {
  const keys = useIdempotencyKeys();
  const [stage, setStage] = useState('start'); // start | card | retry | released
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await keys.run('payment', {reservation: reservation.id}, (idempotencyKey) =>
        createPayment(reservation.id, {idempotencyKey}),
      );
      setIntent(p);
      setStage('card');
    } catch (err) {
      setError(problemMessage(err, 'Could not start the payment. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  // Resolves when aidb reports the payment paid; throws otherwise.
  const confirm = async () => {
    const status = await keys.run('confirm-payment', {reservation: reservation.id}, (idempotencyKey) =>
      confirmPayment(reservation.id, {idempotencyKey}),
    );
    if (!status.paid) throw new Error(PROCESSING);
    onPaid();
  };

  const showReleased = () => {
    setError(null);
    setStage('released');
    if (onReleased) onReleased();
  };

  // Called by StripeCheckout after Stripe took the card.
  const onCardPaid = async () => {
    try {
      await confirm();
    } catch (err) {
      if (err.message === PROCESSING) throw err; // StripeCheckout shows it; Pay stays available
      if (isReleasedConflict(err)) return showReleased();
      setError(problemMessage(err));
      setStage('retry');
    }
  };

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await confirm();
    } catch (err) {
      if (isReleasedConflict(err)) return showReleased();
      setError(problemMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'card' && intent) {
    return (
      <StripeCheckout
        clientSecret={intent.client_secret}
        publishableKey={intent.publishable_key}
        amountCents={intent.amount_cents}
        onPaid={onCardPaid}
      />
    );
  }

  if (stage === 'released') {
    return (
      <div role="alert">
        <p>{RELEASED_COPY.body}</p>
      </div>
    );
  }

  if (stage === 'retry') {
    return (
      <div role="alert">
        <p className="text-danger" style={{whiteSpace: 'pre-line'}}>
          {error}
        </p>
        <p>
          Your card may already have been charged. Retrying confirms the payment and does not charge you
          again.
        </p>
        <Button variant="primary" onClick={retry} disabled={busy}>
          {busy ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <p role="alert" className="text-danger" style={{whiteSpace: 'pre-line'}}>
          {error}
        </p>
      )}
      <Button variant="primary" onClick={start} disabled={busy}>
        {busy ? 'Preparing payment…' : 'Complete payment'}
      </Button>
    </div>
  );
}

ReservationPayment.propTypes = {
  reservation: PropTypes.object.isRequired,
  onPaid: PropTypes.func.isRequired,
  onReleased: PropTypes.func,
};
